/**
 * All Training Records Page - Admin/HR view of all employee training records
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { learningApi } from '../api/learningApi'
import type { VerificationStatus } from '../types/learning.types'
import LoadingPage from '@/shared/components/LoadingPage'
import toast from 'react-hot-toast'

const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
}

const VERIFICATION_STATUS_COLORS: Record<VerificationStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const TRAINING_TYPE_LABELS: Record<string, string> = {
  course: 'Course',
  workshop: 'Workshop',
  certification: 'Certification',
  webinar: 'Webinar',
  seminar: 'Seminar',
  conference: 'Conference',
  other: 'Other',
}

const TRAINING_TYPE_COLORS: Record<string, string> = {
  course: 'bg-blue-100 text-blue-700',
  workshop: 'bg-purple-100 text-purple-700',
  certification: 'bg-emerald-100 text-emerald-700',
  webinar: 'bg-orange-100 text-orange-700',
  seminar: 'bg-pink-100 text-pink-700',
  conference: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-700',
}

type SortField = 'employee' | 'training' | 'type' | 'date' | 'time' | 'rating' | 'status' | 'progress'
type SortDirection = 'asc' | 'desc'

const AllTrainingRecordsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<VerificationStatus | ''>(
    (searchParams.get('status') as VerificationStatus) || ''
  )
  const [yearFilter, setYearFilter] = useState<number | ''>(currentYear)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectingRecordId, setRejectingRecordId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const limit = 20

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Generate year options (current year and 4 years back)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i)

  // Fetch all training records
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['allTrainingRecords', page, statusFilter, yearFilter, searchQuery],
    queryFn: () => learningApi.getAllRecords({
      page,
      limit,
      verification_status: statusFilter || undefined,
      year: yearFilter || undefined,
      search: searchQuery || undefined,
    }),
  })

  // Verify/reject mutation
  const verifyMutation = useMutation({
    mutationFn: ({ id, action, rejection_reason }: { id: string; action: 'verify' | 'reject'; rejection_reason?: string }) =>
      learningApi.verifyRecord(id, action, rejection_reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['allTrainingRecords'] })
      refetch()
      toast.success(`Training record ${variables.action === 'verify' ? 'verified' : 'rejected'} successfully`)
      setShowRejectModal(false)
      setRejectingRecordId(null)
      setRejectionReason('')
    },
    onError: () => {
      toast.error('Failed to update verification status')
    },
  })

  const handleOpenRejectModal = (recordId: string) => {
    setRejectingRecordId(recordId)
    setShowRejectModal(true)
  }

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }
    if (rejectingRecordId) {
      verifyMutation.mutate({ id: rejectingRecordId, action: 'reject', rejection_reason: rejectionReason })
    }
  }

  const records = data?.data || []
  const pagination = data?.pagination

  // Sort records
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedRecords = [...records].sort((a, b) => {
    if (!sortField) return 0

    const direction = sortDirection === 'asc' ? 1 : -1

    switch (sortField) {
      case 'employee':
        return direction * (a.employee?.full_name || '').localeCompare(b.employee?.full_name || '')
      case 'training':
        return direction * a.training.name.localeCompare(b.training.name)
      case 'type':
        return direction * (a.training.training_type || '').localeCompare(b.training.training_type || '')
      case 'date':
        return direction * (new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
      case 'time':
        return direction * ((a.training.duration_hours || 0) - (b.training.duration_hours || 0))
      case 'rating':
        return direction * ((a.rating || 0) - (b.rating || 0))
      case 'status':
        return direction * a.verification_status.localeCompare(b.verification_status)
      case 'progress':
        return direction * ((a.employee_completion_percentage || 0) - (b.employee_completion_percentage || 0))
      default:
        return 0
    }
  })

  // Group records by employee for summary view
  const employeeSummary = records.reduce((acc, record) => {
    const employeeId = record.employee?.id
    if (!employeeId) return acc
    if (!acc[employeeId]) {
      acc[employeeId] = {
        employee: record.employee!,
        totalTrainings: 0,
        verifiedCount: 0,
        pendingCount: 0,
      }
    }
    acc[employeeId].totalTrainings++
    if (record.verification_status === 'verified') {
      acc[employeeId].verifiedCount++
    } else if (record.verification_status === 'pending') {
      acc[employeeId].pendingCount++
    }
    return acc
  }, {} as Record<string, { employee: typeof records[0]['employee'], totalTrainings: number, verifiedCount: number, pendingCount: number }>)

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  const renderStars = (rating: number | undefined) => {
    if (!rating) return <span className="text-gray-400 text-xs">-</span>
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-3.5 h-3.5 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  if (isLoading) return <LoadingPage />

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">All Training Records</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">All Training Records</h1>
              <p className="text-red-200 mt-1">View and manage training records across all employees</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold text-white">{pagination?.total || 0}</p>
                <p className="text-xs text-red-200">Total Records</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold text-white">{Object.keys(employeeSummary).length}</p>
                <p className="text-xs text-red-200">Employees</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by employee name or training..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full md:w-48">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as VerificationStatus | '')
                  setPage(1)
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Year Filter */}
            <div className="w-full md:w-36">
              <select
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value ? parseInt(e.target.value) : '')
                  setPage(1)
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Years</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th
                  className="w-[15%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('employee')}
                >
                  <div className="flex items-center gap-1">
                    Employee
                    <SortIcon field="employee" />
                  </div>
                </th>
                <th
                  className="w-[18%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('training')}
                >
                  <div className="flex items-center gap-1">
                    Training
                    <SortIcon field="training" />
                  </div>
                </th>
                <th
                  className="w-[10%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center gap-1">
                    Type
                    <SortIcon field="type" />
                  </div>
                </th>
                <th
                  className="w-[11%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    <SortIcon field="date" />
                  </div>
                </th>
                <th
                  className="w-[6%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('time')}
                >
                  <div className="flex items-center gap-1">
                    Time
                    <SortIcon field="time" />
                  </div>
                </th>
                <th
                  className="w-[9%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('rating')}
                >
                  <div className="flex items-center gap-1">
                    Rating
                    <SortIcon field="rating" />
                  </div>
                </th>
                <th
                  className="w-[8%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th
                  className="w-[11%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('progress')}
                >
                  <div className="flex items-center gap-1">
                    Progress
                    <SortIcon field="progress" />
                  </div>
                </th>
                <th className="w-[12%] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>No training records found</p>
                    </td>
                  </tr>
                ) : (
                  sortedRecords.map((record) => (
                    <tr
                      key={record.id}
                      onClick={() => navigate(`/learning/records/${record.id}`, { state: { from: 'all-records' } })}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                            {record.employee?.full_name?.charAt(0) || record.employee?.email?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{record.employee?.full_name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500 truncate">{record.employee?.department || ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{record.training.name}</p>
                          {record.training.provider && (
                            <p className="text-xs text-gray-500 truncate">{record.training.provider}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TRAINING_TYPE_COLORS[record.training.training_type] || TRAINING_TYPE_COLORS.other}`}>
                          {TRAINING_TYPE_LABELS[record.training.training_type] || record.training.training_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="text-gray-900">{format(new Date(record.start_date), 'MMM d, yyyy')}</p>
                          {record.end_date && (
                            <p className="text-xs text-gray-500">to {format(new Date(record.end_date), 'MMM d')}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {record.training.duration_hours ? (
                          <span className="text-sm text-gray-900">{record.training.duration_hours}h</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {renderStars(record.rating)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${VERIFICATION_STATUS_COLORS[record.verification_status]}`}>
                          {VERIFICATION_STATUS_LABELS[record.verification_status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {record.employee_completion_percentage !== null && record.employee_completion_percentage !== undefined ? (
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-300 ${
                                  record.employee_completion_percentage >= 100
                                    ? 'bg-green-500'
                                    : record.employee_completion_percentage >= 50
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(record.employee_completion_percentage, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600 w-8">{record.employee_completion_percentage}%</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {record.verification_status === 'pending' ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => verifyMutation.mutate({ id: record.id, action: 'verify' })}
                              disabled={verifyMutation.isPending}
                              className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                              title="Verify"
                            >
                              Verify
                            </button>
                            <button
                              onClick={() => handleOpenRejectModal(record.id)}
                              disabled={verifyMutation.isPending}
                              className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                              title="Reject"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="px-4 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} records
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium text-gray-900">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reject Training Record</h3>
                <p className="text-sm text-gray-500">Please provide a reason for rejection</p>
              </div>
            </div>

            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter the reason for rejecting this training record..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
              autoFocus
            />

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectingRecordId(null)
                  setRejectionReason('')
                }}
                className="flex-1 px-4 py-2.5 text-gray-700 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={verifyMutation.isPending || !rejectionReason.trim()}
                className="flex-1 px-4 py-2.5 text-white font-medium bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {verifyMutation.isPending ? 'Rejecting...' : 'Reject Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AllTrainingRecordsPage
